import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { FOCUSABLE_SELECTOR, wrapTabFocus } from '../../lib/focusTrap';
import { c } from '../../styles/theme';

/**
 * Reusable modal dialog.
 *
 * Replaces five hand-rolled, copy-pasted dialog shells in the prototype.
 * That duplication is precisely why a layering bug existed in all four of
 * the copies at once: each used `position: absolute` with no `z-index`.
 *
 * Fixes rolled into this one component:
 *
 *   - Rendered through a portal onto document.body, so it escapes the
 *     `.lyx-main` scroll container. Previously an `inset: 0` absolute
 *     overlay inside a scrolled container rendered off-screen — scroll
 *     down the Products page, trigger the out-of-credits dialog, and it
 *     appeared above the visible viewport.
 *   - `position: fixed` with an explicit z-index above the Studio drawer
 *     (61). The "Delete song" button inside that drawer previously opened
 *     a confirmation the user could not see at all.
 *   - role="dialog" + aria-modal + a labelled title, so assistive tech
 *     announces it as a dialog.
 *   - Focus moves into the dialog on open and is restored to the
 *     previously focused element on close.
 *   - Focus is trapped: Tab / Shift+Tab cycle within the dialog rather
 *     than wandering behind the overlay.
 *   - Escape closes. Previously only the Studio drawer honoured Escape;
 *     the five dialogs could not be dismissed from the keyboard.
 *   - Background scroll is locked while open.
 *
 * The visual design is unchanged: same surface, border, radius, padding,
 * width clamp (.lyx-modal-box) and overlay colour as the original.
 *
 * ONE CONSEQUENCE OF THE PORTAL, worth spelling out because it caused a real
 * contrast bug: `color` and `font-family` are set on AppShell's own root div,
 * and a portal into document.body is outside that div, so nothing inside a
 * dialog inherits either. Text with no explicit colour rendered as the UA
 * default — black on #17181D — and in the UA serif font. So the box sets both
 * itself. Dialog bodies may still override locally (c.text2 for secondary
 * copy); what they no longer have to do is remember to set a colour at all.
 */

/*
  The Tab-stop selector, now from lib/focusTrap.ts. This list was here and was copied — at
  two different lengths — into the Vault pack drawer and the nav drawer, so the app had three
  answers to "what is focusable". Same six selectors, one definition; see that file's header.
*/
const FOCUSABLE = FOCUSABLE_SELECTOR;

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  /** Renders a visible heading inside the dialog and labels it. */
  title?: string;
  /** Optional control aligned to the far right of the visible title row. */
  headerAction?: ReactNode;
  /** Explicit id for that heading, when something else must reference it. */
  titleId?: string;
  children?: ReactNode;
  /** id of an existing element that names the dialog. Wins over `title`. */
  labelledBy?: string;
  /** id of an element describing the dialog. */
  describedBy?: string;
  /**
   * Accessible name when the dialog body already renders its own visible
   * heading. Use this instead of `title` to avoid rendering the heading twice.
   */
  ariaLabel?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  /**
   * Widens the dialog past the default 340px clamp from .lyx-modal-box.
   *
   * A number is treated as a MAXIMUM, not a fixed width — see clampedWidth. Pass a
   * string only for a value that is already viewport-relative.
   */
  width?: number | string;
}

/**
 * Turns a `width` prop into something that cannot overflow a phone.
 *
 * A plain `width: 440` is an inline style, and an inline style outranks a media
 * query — so .lyx-modal-box's `min(94vw, 320px)` at 560px could not rein it in, and
 * the share dialog stayed 440px wide inside a 380px viewport with its Copy button
 * off the edge. Wrapping the number in min(…, 92vw) keeps the intent (be wider than
 * the default when there is room) while making the viewport the ceiling.
 *
 * 92vw rather than 100vw leaves the overlay's 16px padding visible either side, so
 * the dialog still reads as a layer over the page rather than as a new screen.
 */
function clampedWidth(width: number | string): string {
  return typeof width === 'number' ? `min(${width}px, 92vw)` : width;
}

export default function Modal({
  open,
  onClose,
  title,
  headerAction,
  titleId,
  children,
  labelledBy,
  describedBy,
  ariaLabel,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  width,
}: ModalProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const autoId = useRef(`lyx-modal-${Math.random().toString(36).slice(2, 9)}`);
  const headingId = titleId || autoId.current;

  // Focus management: remember what was focused, move focus in, restore on close.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const box = boxRef.current;
    if (box) {
      const first = box.querySelector(FOCUSABLE);
      if (first instanceof HTMLElement) {
        first.focus();
      } else {
        box.focus();
      }
    }

    return () => {
      const target = previouslyFocused.current;
      if (target && document.contains(target)) target.focus();
    };
  }, [open]);

  // Lock background scroll while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape' && closeOnEscape) {
        e.stopPropagation();
        onClose?.();
        return;
      }

      if (e.key !== 'Tab') return;

      const box = boxRef.current;
      if (!box) return;

      // The wrap itself, from lib/focusTrap.ts — the same call the nav drawer, the Vault pack
      // drawer and the full-screen player make. `e` is React's synthetic event and the helper
      // only reads `shiftKey`/`preventDefault`, which it types structurally for that reason.
      wrapTabFocus(box, e);
    },
    [closeOnEscape, onClose]
  );

  if (!open) return null;

  return createPortal(
    <div
      className="lyx-modal-overlay"
      onMouseDown={(e) => {
        // Only a click that both starts and ends on the overlay dismisses,
        // so dragging a text selection out of the dialog does not close it.
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose?.();
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={boxRef}
        className="lyx-modal-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || (title ? headingId : undefined)}
        aria-label={!labelledBy && !title ? ariaLabel : undefined}
        aria-describedby={describedBy}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 14,
          padding: 22,
          maxHeight: 'calc(100dvh - 32px)',
          overflowY: 'auto',
          // Not inherited through the portal — see the note in the header.
          color: c.text1,
          fontFamily: 'Inter, sans-serif',
          ...(width ? { width: clampedWidth(width) } : null),
        }}
      >
        {title ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              margin: '0 0 8px',
            }}
          >
            <p
              id={headingId}
              style={{
                fontFamily: 'Space Grotesk',
                fontWeight: 600,
                fontSize: 15,
                margin: 0,
              }}
            >
              {title}
            </p>
            {headerAction}
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body
  );
}
